import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  Layers, FileText, Wand2, ShieldCheck, Users, MapPin, Sparkles,
  Check, X, MessageSquare, Image as ImageIcon, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS_TIMELINE = {
  cctp_received: FileText,
  analysis: Wand2,
  validation: ShieldCheck,
  generation: Wand2,
  client_review: Users,
  delivery: ShieldCheck,
};

/**
 * PublicShareView — Vue Universelle (smooth/light) pour partage client externe.
 * Route: /share/:token
 *
 * Aucune auth — token opaque côté URL. Force theme=smooth (luxueux, épuré).
 * Affiche la timeline du projet + la matériauthèque avec la possibilité pour
 * le client de poser un statut (approuvé / refusé / commenté + image référence).
 */
const PublicShareView = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeMaterial, setActiveMaterial] = useState(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'smooth');
    document.body.style.background = '#F7F4EE';
    return () => {
      document.documentElement.setAttribute('data-theme', 'dark');
      document.body.style.background = '';
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/share/${token}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || 'Lien invalide ou révoqué'); });
    return () => { cancelled = true; };
  }, [token]);

  const refresh = () => {
    axios.get(`${API}/public/share/${token}`).then(r => setData(r.data)).catch(() => {});
  };

  if (error) {
    return <ShellLight><EmptyError message={error} /></ShellLight>;
  }
  if (!data) {
    return <ShellLight><div className="px-8 py-16 text-center" style={{ color: 'rgba(0,0,0,0.55)' }}>Chargement...</div></ShellLight>;
  }

  // ---- Variante "render" : un rendu isolé partagé au client ----
  if (data.kind === 'render') {
    return (
      <ShellLight project={data.project}>
        <RenderHero
          project={data.project}
          render={data.render}
          feedback={data.feedback}
        />
        <RenderFeedbackBlock
          token={token}
          feedback={data.feedback}
          onSaved={refresh}
        />
      </ShellLight>
    );
  }

  // ---- Variante "project" : timeline + matériauthèque (existant) ----
  return (
    <ShellLight project={data.project}>
      <Hero project={data.project} />
      <TimelineLight stages={data.timeline} />
      <MaterialsList sections={data.sections} onPick={setActiveMaterial} />
      <ClientIterationsSection token={token} />

      {activeMaterial && (
        <FeedbackDialog
          material={activeMaterial}
          token={token}
          onClose={() => setActiveMaterial(null)}
          onSaved={() => { setActiveMaterial(null); refresh(); }}
        />
      )}
    </ShellLight>
  );
};

// ============================================================================
// Light Shell (smooth theme — luxe, épuré, beige)
// ============================================================================
const ShellLight = ({ project, children }) => (
  <div
    data-theme="smooth"
    data-testid="public-share-view"
    style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #F7F4EE 0%, #EFEBE3 100%)',
      fontFamily: 'Manrope, sans-serif',
      color: '#18181B',
    }}
  >
    {/* Topbar minimal */}
    <header
      className="px-5 sm:px-10 py-5 flex items-center justify-between"
      style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: '#B8894A', color: '#fff' }}
        >
          <Layers className="w-4 h-4" strokeWidth={2} />
        </div>
        <div>
          <span
            className="text-[9.5px] font-bold uppercase tracking-[0.22em]"
            style={{ color: '#B8894A' }}
          >
            Vue partagée
          </span>
          <p
            className="font-semibold tracking-tight leading-none mt-0.5"
            style={{ fontFamily: 'Outfit, sans-serif', fontSize: 16 }}
          >
            Peter's Universe
          </p>
        </div>
      </div>
      {project?.name && (
        <span
          className="hidden sm:inline text-[11px] uppercase tracking-[0.18em] font-semibold"
          style={{ color: 'rgba(0,0,0,0.45)' }}
        >
          {project.name}
        </span>
      )}
    </header>

    <main className="max-w-5xl mx-auto px-5 sm:px-10 py-10 sm:py-14 space-y-12">
      {children}
    </main>

    <footer
      className="px-5 sm:px-10 py-6 mt-12"
      style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
    >
      <p className="text-center text-[11px]" style={{ color: 'rgba(0,0,0,0.40)' }}>
        Vue lecture seule · Lien sécurisé · Peter's Universe
      </p>
    </footer>
  </div>
);

const Hero = ({ project }) => (
  <section className="hud-fade-in">
    <span
      className="text-[10px] font-bold uppercase tracking-[0.22em]"
      style={{ color: '#B8894A' }}
    >
      Projet immobilier · Lecture seule
    </span>
    <h1
      className="mt-2 font-semibold tracking-tight"
      style={{
        fontFamily: 'Outfit, sans-serif',
        fontSize: 'clamp(28px, 4vw, 44px)',
        color: '#18181B',
      }}
    >
      {project.name}
    </h1>
    {project.description && (
      <p className="mt-3 text-[15px] leading-relaxed max-w-2xl" style={{ color: 'rgba(0,0,0,0.65)' }}>
        {project.description}
      </p>
    )}
    {project.address && (
      <p className="mt-3 flex items-center gap-1.5 text-[13px]" style={{ color: 'rgba(0,0,0,0.55)' }}>
        <MapPin className="w-3.5 h-3.5" />
        {project.address}
      </p>
    )}
  </section>
);

// ============================================================================
// Timeline (light)
// ============================================================================
const TimelineLight = ({ stages = [] }) => {
  if (!stages.length) return null;
  return (
    <section className="hud-fade-in">
      <h2
        className="font-semibold tracking-tight mb-5"
        style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, color: '#18181B' }}
      >
        Avancement de votre opération
      </h2>
      <ol className="space-y-1">
        {stages.map((s, i) => {
          const Icon = ICONS_TIMELINE[s.key] || ShieldCheck;
          const bg = s.done ? '#B8894A' : s.active ? 'rgba(184,137,74,0.14)' : '#FFFFFF';
          const iconColor = s.done ? '#FFFFFF' : s.active ? '#B8894A' : 'rgba(0,0,0,0.45)';
          return (
            <li key={s.key} className="flex items-start gap-4 py-2.5 relative">
              <div className="relative shrink-0">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{
                    background: bg,
                    border: `1px solid ${s.done ? '#B8894A' : s.active ? 'rgba(184,137,74,0.35)' : 'rgba(0,0,0,0.10)'}`,
                    boxShadow: s.done || s.active ? '0 4px 14px rgba(184,137,74,0.20)' : 'none',
                  }}
                >
                  <Icon className="w-4 h-4" style={{ color: iconColor }} strokeWidth={2} />
                </div>
                {i !== stages.length - 1 && (
                  <span
                    aria-hidden
                    className="absolute left-1/2 -translate-x-1/2 top-10 w-px"
                    style={{ height: 28, background: s.done ? '#B8894A' : 'rgba(0,0,0,0.10)', opacity: s.done ? 0.45 : 1 }}
                  />
                )}
              </div>
              <div className="flex-1 min-w-0 pt-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[14.5px] font-semibold" style={{ color: '#18181B' }}>
                    {s.label}
                  </span>
                  {s.active && <SmoothBadge tone="gold">En cours</SmoothBadge>}
                  {s.done && <SmoothBadge tone="success">Terminé</SmoothBadge>}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

const SmoothBadge = ({ tone = 'gold', children }) => {
  const colors = {
    gold:    { bg: 'rgba(184,137,74,0.14)', fg: '#7C5A2E', bd: 'rgba(184,137,74,0.30)' },
    success: { bg: 'rgba(34,197,94,0.14)',  fg: '#15803D', bd: 'rgba(34,197,94,0.28)' },
  }[tone];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-[1px] text-[10px] font-bold uppercase tracking-[0.16em] rounded"
      style={{ background: colors.bg, color: colors.fg, border: `1px solid ${colors.bd}` }}
    >
      {children}
    </span>
  );
};

// ============================================================================
// Materials list (light)
// ============================================================================
const STATUS_LABEL = {
  pending:   { label: 'À valider',   color: 'rgba(0,0,0,0.45)', bg: 'rgba(0,0,0,0.04)' },
  approved:  { label: 'Approuvé',    color: '#15803D',           bg: 'rgba(34,197,94,0.14)' },
  refused:   { label: 'Refusé',      color: '#B91C1C',           bg: 'rgba(239,68,68,0.14)' },
  commented: { label: 'Commenté',    color: '#7C5A2E',           bg: 'rgba(184,137,74,0.14)' },
};

const MaterialsList = ({ sections = [], onPick }) => {
  if (!sections.length) return null;
  return (
    <section className="hud-fade-in space-y-8">
      <h2
        className="font-semibold tracking-tight"
        style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, color: '#18181B' }}
      >
        Matériauthèque & finitions
      </h2>
      {sections.map((sec) => (
        <div key={sec.id}>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.22em]"
            style={{ color: '#B8894A' }}
          >
            {sec.label} · {sec.items.length}
          </span>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {sec.items.map((m) => {
              const st = STATUS_LABEL[m.client_status] || STATUS_LABEL.pending;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => onPick(m)}
                  className="text-left rounded-2xl p-4 transition-all"
                  style={{
                    background: '#FFFFFF',
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 24px rgba(184,137,74,0.16)'; e.currentTarget.style.borderColor = 'rgba(184,137,74,0.30)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = 'rgba(0,0,0,0.08)'; }}
                  data-testid={`public-material-${m.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-lg shrink-0"
                      style={{
                        background: m.thumb_color || '#B8894A',
                        border: '1px solid rgba(0,0,0,0.08)',
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13.5px] font-semibold leading-tight truncate" style={{ color: '#18181B' }}>
                        {m.name}
                      </p>
                      <p className="text-[11px] mt-0.5 truncate" style={{ color: 'rgba(0,0,0,0.55)' }}>
                        {[m.dimensions, m.finish].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.18em] rounded"
                      style={{ background: st.bg, color: st.color }}
                    >
                      {st.label}
                    </span>
                    {m.client_comment && (
                      <span className="text-[10.5px] flex items-center gap-1" style={{ color: 'rgba(0,0,0,0.45)' }}>
                        <MessageSquare className="w-2.5 h-2.5" /> remarque
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
};

// ============================================================================
// Feedback dialog (light) — soumet POST /public/share/{token}/materials/{id}/feedback
// ============================================================================
const FeedbackDialog = ({ material, token, onClose, onSaved }) => {
  const [status, setStatus] = useState(material.client_status || 'pending');
  const [comment, setComment] = useState(material.client_comment || '');
  const [imageUrl, setImageUrl] = useState(material.client_reference_image_url || '');
  const [signature, setSignature] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/public/share/${token}/materials/${material.id}/feedback`,
        {
          client_status: status,
          client_comment: comment || null,
          client_reference_image_url: imageUrl || null,
          client_signature: signature || null,
        }
      );
      toast.success('Votre choix a été enregistré');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[1500] px-4"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="public-feedback-dialog"
    >
      <div
        className="rounded-2xl w-full max-w-[520px] overflow-hidden"
        style={{ background: '#FFFFFF', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6" style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <span className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: '#B8894A' }}>
            Votre avis
          </span>
          <h3
            className="mt-1 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', fontSize: 20, color: '#18181B' }}
          >
            {material.name}
          </h3>
          <p className="text-[12.5px] mt-1" style={{ color: 'rgba(0,0,0,0.55)' }}>
            {[material.dimensions, material.finish].filter(Boolean).join(' · ') || 'Choisissez votre statut'}
          </p>
        </div>

        <div className="p-6 space-y-5">
          {/* Status pills */}
          <div className="grid grid-cols-3 gap-2">
            <StatusPill icon={Check}        label="Approuver" tone="success"   active={status === 'approved'} onClick={() => setStatus('approved')} />
            <StatusPill icon={MessageSquare} label="Commenter" tone="gold"      active={status === 'commented'} onClick={() => setStatus('commented')} />
            <StatusPill icon={X}            label="Refuser"   tone="danger"    active={status === 'refused'}  onClick={() => setStatus('refused')} />
          </div>

          {(status === 'commented' || status === 'refused') && (
            <div>
              <label className="text-[12px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'rgba(0,0,0,0.55)' }}>
                Votre remarque
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Ex: Je préférerais une teinte plus chaude, ou une finition mate..."
                rows={3}
                className="mt-1 w-full rounded-lg p-3 text-[13px] outline-none"
                style={{ background: '#FAF8F4', border: '1px solid rgba(0,0,0,0.10)' }}
                data-testid="public-feedback-comment"
              />
            </div>
          )}

          {(status === 'commented' || status === 'refused') && (
            <div>
              <label className="text-[12px] font-semibold uppercase tracking-[0.16em] flex items-center gap-1" style={{ color: 'rgba(0,0,0,0.55)' }}>
                <ImageIcon className="w-3 h-3" />
                Image de référence (lien Pinterest, Drive, etc.)
              </label>
              <input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://..."
                className="mt-1 w-full rounded-lg p-3 text-[13px] outline-none"
                style={{ background: '#FAF8F4', border: '1px solid rgba(0,0,0,0.10)' }}
                data-testid="public-feedback-image"
              />
            </div>
          )}

          <div>
            <label className="text-[12px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'rgba(0,0,0,0.55)' }}>
              Votre nom (signature)
            </label>
            <input
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="ex. Pierre Durand"
              className="mt-1 w-full rounded-lg p-3 text-[13px] outline-none"
              style={{ background: '#FAF8F4', border: '1px solid rgba(0,0,0,0.10)' }}
              data-testid="public-feedback-signature"
            />
          </div>
        </div>

        <div
          className="p-5 flex items-center justify-end gap-2"
          style={{ borderTop: '1px solid rgba(0,0,0,0.08)', background: '#FAF8F4' }}
        >
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 h-9 text-[13px] font-semibold rounded-lg"
            style={{ color: 'rgba(0,0,0,0.55)' }}
            data-testid="public-feedback-cancel"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-5 h-9 text-[13px] font-semibold rounded-lg"
            style={{ background: '#B8894A', color: '#FFFFFF', boxShadow: '0 4px 12px rgba(184,137,74,0.30)' }}
            data-testid="public-feedback-submit"
          >
            {busy ? 'Envoi...' : 'Valider mon choix'}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatusPill = ({ icon: Icon, label, tone, active, onClick }) => {
  const palette = {
    success: { bg: '#15803D', fg: '#FFFFFF', soft: 'rgba(34,197,94,0.10)', dark: '#15803D' },
    gold:    { bg: '#B8894A', fg: '#FFFFFF', soft: 'rgba(184,137,74,0.10)', dark: '#7C5A2E' },
    danger:  { bg: '#B91C1C', fg: '#FFFFFF', soft: 'rgba(239,68,68,0.10)', dark: '#B91C1C' },
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl transition-all text-[11.5px] font-semibold"
      style={{
        background: active ? palette.bg : palette.soft,
        color: active ? palette.fg : palette.dark,
        border: `1px solid ${active ? palette.bg : 'rgba(0,0,0,0.06)'}`,
      }}
      data-testid={`public-status-${tone}`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
};

// ============================================================================
// Render-share variants (a single rendering shared to the client)
// ============================================================================
const RenderHero = ({ project, render, feedback }) => {
  const status = feedback?.client_status || 'pending';
  const statusMeta = {
    pending:   { label: 'En attente',  bg: '#FAF8F4',                   color: 'rgba(0,0,0,0.55)' },
    approved:  { label: 'Approuvé',    bg: 'rgba(34,197,94,0.12)',       color: '#15803D' },
    commented: { label: 'Commenté',    bg: 'rgba(59,130,246,0.12)',      color: '#1D4ED8' },
    refused:   { label: 'Refusé',      bg: 'rgba(239,68,68,0.12)',       color: '#B91C1C' },
  }[status];
  const imgUrl = `${process.env.REACT_APP_BACKEND_URL}${render.image_url}`;
  return (
    <section className="hud-fade-in space-y-6">
      <div>
        <span className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: '#B8894A' }}>
          Rendu IA · Validation client
        </span>
        <h1
          className="mt-2 font-semibold tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(28px, 4vw, 40px)', color: '#18181B' }}
        >
          {project.name}
          {render.lot_number ? <span style={{ color: 'rgba(0,0,0,0.55)' }}> · Lot {render.lot_number}</span> : null}
        </h1>
        {render.typology && (
          <p className="mt-2 text-[13px]" style={{ color: 'rgba(0,0,0,0.55)' }}>
            Typologie {render.typology}
            {render.completed_at ? ` · Généré le ${new Date(render.completed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}` : ''}
          </p>
        )}
      </div>

      <figure
        className="rounded-2xl overflow-hidden"
        style={{ background: '#FFFFFF', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.18)', border: '1px solid rgba(0,0,0,0.05)' }}
      >
        <img
          src={imgUrl}
          alt={`Rendu ${render.lot_number || ''}`}
          className="w-full h-auto block"
          data-testid="public-render-image"
          style={{ maxHeight: '70vh', objectFit: 'contain', background: '#FAF8F4' }}
        />
      </figure>

      <div className="flex items-center gap-2 flex-wrap">
        <span
          data-testid="public-render-status"
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11.5px] font-semibold"
          style={{ background: statusMeta.bg, color: statusMeta.color, border: `1px solid ${statusMeta.color}33` }}
        >
          <ShieldCheck className="w-3 h-3" />
          {statusMeta.label}
        </span>
        {feedback?.client_signed_by && (
          <span className="text-[11.5px]" style={{ color: 'rgba(0,0,0,0.55)' }}>
            par <strong style={{ color: '#18181B' }}>{feedback.client_signed_by}</strong>
            {feedback.client_signed_at ? ` le ${new Date(feedback.client_signed_at).toLocaleDateString('fr-FR')}` : ''}
          </span>
        )}
      </div>

      {Object.values(render.params || {}).some(Boolean) && (
        <div
          className="rounded-2xl p-5"
          style={{ background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.06)' }}
        >
          <span className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: '#B8894A' }}>
            Paramètres de rendu
          </span>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
            {[
              ['Type de bien',   render.params.type_bien],
              ['Style design',   render.params.style_design],
              ['Étage / niveau', render.params.etage_niveau],
              ['Moment journée', render.params.moment_journee],
              ['Vue / angle',    render.params.vue_angle],
              ['Mobilier',       render.params.catalogue_mobilier],
              ['Échelle plan',   render.params.echelle_plan],
            ].filter(([, v]) => v).map(([label, value]) => (
              <li
                key={label}
                className="flex items-baseline justify-between gap-3 py-1"
                style={{ borderBottom: '1px dashed rgba(0,0,0,0.08)' }}
              >
                <span style={{ color: 'rgba(0,0,0,0.55)' }}>{label}</span>
                <span style={{ color: '#18181B' }} className="font-medium text-right truncate">
                  {String(value)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

const RenderFeedbackBlock = ({ token, feedback, onSaved }) => {
  const [open, setOpen] = useState(false);
  const status = feedback?.client_status || 'pending';
  const cta = status === 'pending'
    ? 'Donner mon avis sur ce rendu'
    : 'Modifier mon avis';

  return (
    <section className="hud-fade-in" data-testid="public-render-feedback-section">
      <div
        className="rounded-2xl p-6 sm:p-8 text-center"
        style={{
          background: 'linear-gradient(135deg, #FFFFFF 0%, #FAF6EE 100%)',
          border: '1px solid rgba(184,137,74,0.20)',
          boxShadow: '0 16px 40px -16px rgba(184,137,74,0.20)',
        }}
      >
        <Sparkles className="w-6 h-6 mx-auto" style={{ color: '#B8894A' }} />
        <h3
          className="mt-3 font-semibold tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, color: '#18181B' }}
        >
          Votre validation
        </h3>
        <p className="mt-2 text-[13.5px] max-w-xl mx-auto" style={{ color: 'rgba(0,0,0,0.60)' }}>
          Ce rendu vous convient-il ? Vous pouvez l'<strong>approuver</strong>,
          <strong> commenter</strong> pour suggérer des ajustements ou le <strong>refuser</strong> en demandant
          une nouvelle version. Votre choix sera transmis au studio.
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="public-render-feedback-open"
          className="mt-5 inline-flex items-center gap-2 px-5 h-10 text-[13px] font-semibold rounded-lg transition-transform hover:-translate-y-0.5"
          style={{ background: '#B8894A', color: '#FFFFFF', boxShadow: '0 4px 14px rgba(184,137,74,0.32)' }}
        >
          <ShieldCheck className="w-4 h-4" />
          {cta}
        </button>
        {feedback?.client_comment && (
          <div
            className="mt-5 rounded-xl p-4 text-left max-w-xl mx-auto"
            style={{ background: '#FAF8F4', border: '1px solid rgba(0,0,0,0.06)' }}
          >
            <span className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: '#B8894A' }}>
              Votre commentaire
            </span>
            <p className="mt-1 text-[13px] italic" style={{ color: 'rgba(0,0,0,0.75)' }}>
              “{feedback.client_comment}”
            </p>
          </div>
        )}
      </div>

      {open && (
        <RenderFeedbackDialog
          token={token}
          initial={feedback}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); onSaved?.(); }}
        />
      )}
    </section>
  );
};

const RenderFeedbackDialog = ({ token, initial, onClose, onSaved }) => {
  const [status, setStatus] = useState(initial?.client_status === 'pending' ? 'approved' : (initial?.client_status || 'approved'));
  const [comment, setComment] = useState(initial?.client_comment || '');
  const [signature, setSignature] = useState(initial?.client_signed_by || '');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/public/share/${token}/render-feedback`,
        {
          client_status: status,
          client_comment: comment || null,
          client_signature: signature || null,
        }
      );
      toast.success('Votre choix a été enregistré');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[1500] px-4"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="public-render-feedback-dialog"
    >
      <div
        className="rounded-2xl w-full max-w-[520px] overflow-hidden"
        style={{ background: '#FFFFFF', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6" style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <span className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: '#B8894A' }}>
            Validation
          </span>
          <h3
            className="mt-1 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', fontSize: 20, color: '#18181B' }}
          >
            Votre avis sur ce rendu
          </h3>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-2">
            <StatusPill icon={Check}        label="Approuver" tone="success" active={status === 'approved'}  onClick={() => setStatus('approved')} />
            <StatusPill icon={MessageSquare} label="Commenter" tone="gold"    active={status === 'commented'} onClick={() => setStatus('commented')} />
            <StatusPill icon={X}            label="Refuser"   tone="danger"  active={status === 'refused'}   onClick={() => setStatus('refused')} />
          </div>

          {(status === 'commented' || status === 'refused') && (
            <div>
              <label className="text-[12px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'rgba(0,0,0,0.55)' }}>
                Votre remarque
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Ex: J'aimerais plus de luminosité, ou un autre angle..."
                rows={3}
                className="mt-1 w-full rounded-lg p-3 text-[13px] outline-none"
                style={{ background: '#FAF8F4', border: '1px solid rgba(0,0,0,0.10)' }}
                data-testid="public-render-feedback-comment"
              />
            </div>
          )}

          <div>
            <label className="text-[12px] font-semibold uppercase tracking-[0.16em]" style={{ color: 'rgba(0,0,0,0.55)' }}>
              Votre nom (signature)
            </label>
            <input
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="ex. Pierre Durand"
              className="mt-1 w-full rounded-lg p-3 text-[13px] outline-none"
              style={{ background: '#FAF8F4', border: '1px solid rgba(0,0,0,0.10)' }}
              data-testid="public-render-feedback-signature"
            />
          </div>
        </div>

        <div
          className="p-5 flex items-center justify-end gap-2"
          style={{ borderTop: '1px solid rgba(0,0,0,0.08)', background: '#FAF8F4' }}
        >
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 h-9 text-[13px] font-semibold rounded-lg"
            style={{ color: 'rgba(0,0,0,0.55)' }}
            data-testid="public-render-feedback-cancel"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-5 h-9 text-[13px] font-semibold rounded-lg"
            style={{ background: '#B8894A', color: '#FFFFFF', boxShadow: '0 4px 12px rgba(184,137,74,0.30)' }}
            data-testid="public-render-feedback-submit"
          >
            {busy ? 'Envoi...' : 'Valider'}
          </button>
        </div>
      </div>
    </div>
  );
};


const EmptyError = ({ message }) => (
  <div className="px-8 py-20 text-center">
    <AlertCircle className="w-10 h-10 mx-auto mb-3" style={{ color: '#B91C1C' }} />
    <h2
      className="font-semibold tracking-tight"
      style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, color: '#18181B' }}
    >
      Lien invalide
    </h2>
    <p className="mt-2 text-[13px]" style={{ color: 'rgba(0,0,0,0.55)' }}>
      {message}
    </p>
  </div>
);

export default PublicShareView;

// ============================================================================
// ClientIterationsSection — Vue Universelle Phase 3.6
// Affiche la timeline lecture seule des itérations visibles + 2 actions
// rapides (Valider / Demander modification) qui créent automatiquement une
// nouvelle Itération côté backend tagguée `source=client_universal_view`.
// ============================================================================

const ITERATION_TYPE_META_LIGHT = {
  brief_update: { label: 'Brief mis à jour', color: '#3B82F6' },
  review:       { label: 'Revue',            color: '#B8894A' },
  validation:   { label: 'Validation',       color: '#16A34A' },
  retake:       { label: 'Retake',           color: '#EA580C' },
  delivery:     { label: 'Livraison',        color: '#7C3AED' },
  note:         { label: 'Note',             color: '#71717A' },
};

const ITERATION_STATUS_LABELS = {
  en_attente:  'En attente',
  validee:     'Validé',
  a_reprendre: 'À reprendre',
  rejetee:     'Rejeté',
};

const ClientIterationsSection = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null); // 'validate' | 'request_revision' | null
  const [dialogOpen, setDialogOpen] = useState(null); // action key when open
  const [form, setForm] = useState({ comment: '', signature: '' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/public/share/${token}/iterations`);
      setItems(r.data?.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const openAction = (action) => {
    setForm({ comment: '', signature: '' });
    setDialogOpen(action);
  };
  const closeAction = () => setDialogOpen(null);

  const submitAction = async () => {
    if (!form.signature.trim()) {
      toast.error('Merci d\'indiquer votre nom pour signer cette action');
      return;
    }
    setSubmitting(dialogOpen);
    try {
      await axios.post(`${API}/public/share/${token}/iterations/quick`, {
        action: dialogOpen,
        comment: form.comment.trim(),
        client_signature: form.signature.trim(),
      });
      toast.success(dialogOpen === 'validate' ? 'Validation envoyée' : 'Demande envoyée');
      closeAction();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    } finally { setSubmitting(null); }
  };

  return (
    <section className="hud-fade-in" data-testid="public-iterations-section">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
        <div>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.22em]"
            style={{ color: '#B8894A' }}
          >
            Suivi & validations
          </span>
          <h2
            className="mt-2 font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif', fontSize: 26, color: '#18181B' }}
          >
            Journal du projet
          </h2>
          <p className="mt-1 text-[13px] max-w-2xl" style={{ color: 'rgba(0,0,0,0.55)' }}>
            Suivez l'avancement et validez les livraisons en un clic. Chaque
            action est enregistrée et signée.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => openAction('request_revision')}
            disabled={submitting !== null}
            className="px-4 py-2 rounded-lg text-[13px] font-medium transition-colors"
            style={{
              background: '#fff',
              border: '1px solid rgba(0,0,0,0.12)',
              color: '#EA580C',
            }}
            data-testid="public-request-revision-btn"
          >
            <span className="inline-flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" />
              Demander une modification
            </span>
          </button>
          <button
            onClick={() => openAction('validate')}
            disabled={submitting !== null}
            className="px-4 py-2 rounded-lg text-[13px] font-medium transition-colors text-white"
            style={{
              background: '#16A34A',
              border: '1px solid #16A34A',
              boxShadow: '0 4px 12px -4px rgba(22,163,74,0.4)',
            }}
            data-testid="public-validate-btn"
          >
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" />
              Valider l'étape
            </span>
          </button>
        </div>
      </div>

      <div
        className="rounded-2xl p-5 sm:p-6"
        style={{
          background: '#fff',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        {loading ? (
          <p className="text-[13px] py-6 text-center" style={{ color: 'rgba(0,0,0,0.45)' }}>
            Chargement…
          </p>
        ) : items.length === 0 ? (
          <div className="py-8 text-center" data-testid="public-iterations-empty">
            <ShieldCheck className="w-8 h-8 mx-auto mb-2" style={{ color: '#B8894A' }} />
            <p className="text-[13px] font-medium" style={{ color: '#18181B' }}>
              Aucune étape ouverte pour validation
            </p>
            <p className="text-[11.5px] mt-1" style={{ color: 'rgba(0,0,0,0.45)' }}>
              Utilisez les boutons ci-dessus pour valider ou demander une modification.
            </p>
          </div>
        ) : (
          <ol className="relative ml-2 space-y-3" data-testid="public-iterations-list">
            <span
              aria-hidden
              className="absolute left-[7px] top-2 bottom-2 w-px"
              style={{ background: 'rgba(0,0,0,0.08)' }}
            />
            {items.map((it) => {
              const meta = ITERATION_TYPE_META_LIGHT[it.type] || ITERATION_TYPE_META_LIGHT.note;
              return (
                <li
                  key={it.iteration_id}
                  className="relative pl-7"
                  data-testid={`public-iteration-row-${it.iteration_id}`}
                >
                  <span
                    aria-hidden
                    className="absolute left-0 top-2 w-[15px] h-[15px] rounded-full"
                    style={{
                      background: '#fff',
                      border: `1.5px solid ${meta.color}`,
                    }}
                  />
                  <div
                    className="rounded-lg p-3"
                    style={{ background: '#F7F4EE', border: '1px solid rgba(0,0,0,0.06)' }}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span
                        className="text-[10px] uppercase tracking-[0.16em] font-bold"
                        style={{ color: meta.color }}
                      >
                        {meta.label}
                      </span>
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                        style={{
                          background: 'rgba(0,0,0,0.06)',
                          color: 'rgba(0,0,0,0.65)',
                        }}
                      >
                        {ITERATION_STATUS_LABELS[it.status] || it.status}
                      </span>
                      <span className="text-[10.5px]" style={{ color: 'rgba(0,0,0,0.40)' }}>
                        {fmtPublicDate(it.created_at)}
                      </span>
                      {it.client_signed_by && (
                        <span className="text-[10.5px] italic" style={{ color: '#B8894A' }}>
                          signé · {it.client_signed_by}
                        </span>
                      )}
                    </div>
                    <p className="text-[14px] font-medium" style={{ color: '#18181B' }}>
                      {it.title}
                    </p>
                    {it.description && (
                      <p className="text-[12.5px] mt-1 whitespace-pre-wrap leading-relaxed" style={{ color: 'rgba(0,0,0,0.62)' }}>
                        {it.description}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Action dialog */}
      {dialogOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeAction(); }}
          data-testid="public-action-dialog"
        >
          <div
            className="rounded-2xl p-6 max-w-md w-full"
            style={{ background: '#fff', boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)' }}
          >
            <h3
              className="font-semibold tracking-tight mb-1"
              style={{ fontFamily: 'Outfit, sans-serif', fontSize: 20, color: '#18181B' }}
            >
              {dialogOpen === 'validate' ? 'Valider l\'étape' : 'Demander une modification'}
            </h3>
            <p className="text-[13px] mb-4" style={{ color: 'rgba(0,0,0,0.55)' }}>
              {dialogOpen === 'validate'
                ? 'Cette action sera enregistrée comme validation signée.'
                : 'Décrivez ce qui doit être modifié — l\'équipe sera notifiée.'}
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] font-semibold block mb-1" style={{ color: 'rgba(0,0,0,0.55)' }}>
                  Votre nom (signature) *
                </label>
                <input
                  value={form.signature}
                  onChange={(e) => setForm((f) => ({ ...f, signature: e.target.value }))}
                  placeholder="Ex. Mme Dupont"
                  className="w-full px-3 py-2 rounded-lg text-[13px] outline-none"
                  style={{
                    background: '#F7F4EE',
                    border: '1px solid rgba(0,0,0,0.08)',
                    color: '#18181B',
                  }}
                  data-testid="public-signature-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] font-semibold block mb-1" style={{ color: 'rgba(0,0,0,0.55)' }}>
                  {dialogOpen === 'validate' ? 'Commentaire (optionnel)' : 'Détails de la demande'}
                </label>
                <textarea
                  value={form.comment}
                  onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                  rows={4}
                  placeholder={dialogOpen === 'validate'
                    ? 'Un mot pour l\'équipe…'
                    : 'Ex. Cuisine : ajouter une suspension au-dessus de l\'îlot.'}
                  className="w-full px-3 py-2 rounded-lg text-[13px] outline-none resize-y"
                  style={{
                    background: '#F7F4EE',
                    border: '1px solid rgba(0,0,0,0.08)',
                    color: '#18181B',
                  }}
                  data-testid="public-comment-input"
                />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={closeAction}
                disabled={!!submitting}
                className="px-4 py-2 rounded-lg text-[13px] font-medium"
                style={{ background: '#F7F4EE', color: '#18181B', border: '1px solid rgba(0,0,0,0.08)' }}
                data-testid="public-dialog-cancel"
              >
                Annuler
              </button>
              <button
                onClick={submitAction}
                disabled={!!submitting}
                className="px-4 py-2 rounded-lg text-[13px] font-medium text-white"
                style={{
                  background: dialogOpen === 'validate' ? '#16A34A' : '#EA580C',
                  border: '1px solid currentColor',
                  opacity: submitting ? 0.6 : 1,
                }}
                data-testid="public-dialog-submit"
              >
                {submitting ? 'Envoi…' : (dialogOpen === 'validate' ? 'Valider' : 'Envoyer la demande')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const fmtPublicDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso.slice(0, 16); }
};
